import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Check, CheckCheck } from 'lucide-react';
import api from '../services/api';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications');
      if (res.data) {
        setNotifications(res.data.notifications || []);
        const unread = (res.data.notifications || []).filter(n => !n.read).length;
        setUnreadCount(unread);
      }
    } catch (err) {
      // Silent fail
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      // Network or 401 — keep local UI optimistic-update silent;
      // a refresh interval (30s) will reconcile the count.
      console.warn('markAsRead failed:', err?.message || err);
    }
  };

  const markAllRead = async () => {
    setLoading(true);
    try {
      await api.put('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      // Same reasoning as markAsRead — refresh interval reconciles state.
      console.warn('markAllRead failed:', err?.message || err);
    }
    setLoading(false);
  };

  const getTimeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Ahora';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  const getIcon = (type) => {
    switch (type) {
      case 'content': return 'fa-play-circle text-amber-400';
      case 'course': return 'fa-graduation-cap text-blue-400';
      case 'reminder': return 'fa-clock text-orange-400';
      case 'achievement': return 'fa-trophy text-yellow-400';
      case 'system': return 'fa-gear text-zinc-400';
      default: return 'fa-bell text-amber-400';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-zinc-800 transition-colors text-zinc-400 hover:text-white"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center min-w-[18px] h-[18px] px-1" data-testid="notification-count">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 bottom-full mb-2 w-80 max-h-96 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden z-50" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
            <span className="text-white text-sm font-semibold">Notificaciones</span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                disabled={loading}
                className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"
                data-testid="mark-all-read"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Marcar todo leído
              </button>
            )}
          </div>

          {/* Notifications list */}
          <div className="overflow-y-auto max-h-72">
            {notifications.length === 0 ? (
              <div className="py-10 text-center">
                <Bell className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">Sin notificaciones</p>
              </div>
            ) : (
              notifications.map(n => (
                <button
                  key={n.id}
                  onClick={() => { if (!n.read) markAsRead(n.id); }}
                  className={`w-full text-left px-4 py-3 flex gap-3 transition-colors border-b border-zinc-800/50 ${
                    n.read ? 'opacity-60' : 'bg-zinc-800/30 hover:bg-zinc-800/60'
                  }`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <i className={`fa-solid ${getIcon(n.type)} text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs font-medium truncate">{n.title}</p>
                    <p className="text-zinc-400 text-[11px] mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-zinc-600 text-[10px] mt-1">{getTimeAgo(n.created_at)}</p>
                  </div>
                  {!n.read && (
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
